import React, { useState, useEffect } from 'react';

import { useForm } from 'react-hook-form';

import resource from '../resources/resource.json';

import { getCartState } from '@msdyn365-commerce/global-state';
import { SimpleProduct, AsyncResult } from '@msdyn365-commerce/retail-proxy';

import { deleteBTGPCartLineAsync, getAllBTGPCartLineAsync } from '../../../actions/DataActionExtension.g';

import { IField, IdState } from '../ecomm-product-details-form.data';
import { IEcommProductDetailsFormConfig } from '../ecomm-product-details-form.props.autogenerated';
import { toast } from 'react-toastify';

interface IEcommFormProps {
    formScheme: IField[];
    selectedProduct: AsyncResult<SimpleProduct>;
    rowsData: IdState[];
    addRow(noOfRows: Number): void;
    deleteRow(idx: number): void;
    callbackName?: any;
    callbackContact?: any;
    callbackEmail?: any;
    callbackDate?: any;
    callbackNamesEntered?: any;
    formItems: [];
    config: IEcommProductDetailsFormConfig;
    localeCode: string;
    actionContext: any;
    isMobile: boolean;
    isEditMode: boolean;
    allowMultipleRegistrations: boolean;
    showRemarksField: boolean;
    callbackFormValidation?: (isValid: boolean) => void;
}

const CustomerContactForm: React.FC<IEcommFormProps> = ({
    formScheme,
    selectedProduct,
    rowsData,
    addRow,
    deleteRow,
    callbackName,
    callbackContact,
    callbackEmail,
    callbackDate,
    callbackNamesEntered,
    formItems,
    config,
    localeCode,
    actionContext,
    isMobile,
    isEditMode,
    allowMultipleRegistrations,
    showRemarksField,
    callbackFormValidation
}) => {
    const [customerContactFormName, setCustomerContactFormName] = useState(['']);
    const [customerContactFormContact, setCustomerContactFormContact] = useState(['']);
    const [customerContactFormEmail, setCustomerContactFormEmail] = useState(['']);
    const [customerContactFormDate, setCustomerContactFormDate] = useState(['']);

    const formLabels = resource.customerContactForm[localeCode as 'en' | 'zh'];

    // validation regex patterns
    const nameRegex = /^[A-Za-z\s]*$/;
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    //const emailRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[._%+-])[A-Za-z\d._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
    const contactRegex = /^[0-9]*$/;
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])[/-](0[1-9]|1[0-2])[/-](\d{4})$/;

    const [isNameValid, setIsNameValid] = useState(true);
    const [isContactValid, setIsContactValid] = useState(true);
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isDateValid, setIsDateValid] = useState(true);

    const onSubmit = (data: any) => {
        // display form data on success
        // alert('SUCCESS!! :-)\n\n' + JSON.stringify(data, null, 4));
    };

    // functions to build form returned by useForm() hook
    const { handleSubmit, reset } = useForm();

    const _setCustomerContactFormNameInputValue = (event: any, index: number) => {
        const maxLength = 40; // fixed character limit

        let inputValue = event.target.value;

        if (inputValue.length > maxLength) {
            toast.warning('Name cannot exceed 40 characters.');
            inputValue = inputValue.substring(0, maxLength);
        }

        _setCustomerContactFormNameArray(inputValue, index);
    };

    const _setCustomerContactFormNameArray = (name: string, index: number) => {
        const offeringNameArray = customerContactFormName.slice();

        while (typeof offeringNameArray[`${index}`] === 'undefined') {
            offeringNameArray.push('');
        }

        offeringNameArray[`${index}`] = name.trim();

        setCustomerContactFormName(offeringNameArray);

        callbackName(offeringNameArray);

        _checkNamesEntered(offeringNameArray);
    };

    const _setCustomerContactFormContactInputValue = (event: any, index: number) => {
        const maxLength = 10;
        let inputValue = event.target.value;

        if (inputValue.length > maxLength) {
            toast.warning('Contact number cannot exceed 10 digits.');
            inputValue = inputValue.substring(0, maxLength);
        }

        _setCustomerContactFormContactArray(inputValue, index);
    };

    const _setCustomerContactFormContactArray = (name: string, index: number) => {
        const offeringContactArray = customerContactFormContact.slice();

        while (typeof offeringContactArray[`${index}`] === 'undefined') {
            offeringContactArray.push('');
        }

        offeringContactArray[`${index}`] = name.trim();

        setCustomerContactFormContact(offeringContactArray);

        callbackContact(offeringContactArray);
    };

    const _setcustomerContactFormEmailInputValue = (event: any, index: number) => {
        const maxLength = config.charactersLimitForRemarks ? config.charactersLimitForRemarks : 100;
        let inputValue = event.target.value.substring(0, maxLength - 1);

        // Prevent typing anything after a valid TLD (e.g., after ".com")
        const match = inputValue.match(/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/);
        if (match) {
            // Lock value at the valid email end
            inputValue = match[0];
        }

        _setcustomerContactFormEmailArray(inputValue, index);
    };

    const _setcustomerContactFormEmailArray = (name: string, index: number) => {
        const offeringEmailArray = customerContactFormEmail.slice();

        while (typeof offeringEmailArray[`${index}`] === 'undefined') {
            offeringEmailArray.push('');
        }

        offeringEmailArray[`${index}`] = name.trim();

        setCustomerContactFormEmail(offeringEmailArray);

        callbackEmail(offeringEmailArray);
    };

    const _setcustomerContactFormDateInputValue = (event: any, index: number) => {
        const inputValue = event.target.value.trim();

        _setcustomerContactFormDateArray(inputValue, index);
    };

    const _setcustomerContactFormDateArray = (name: string, index: number) => {
        const offeringDateArray = customerContactFormDate.slice();

        while (typeof offeringDateArray[`${index}`] === 'undefined') {
            offeringDateArray.push('');
        }

        offeringDateArray[`${index}`] = name.trim();

        setCustomerContactFormDate(offeringDateArray);

        callbackDate(offeringDateArray);
    };

    // --- ONBLUR HANDLERS (Validate when user leaves field) ---

    const _validateCustomerContactFormName = (event: any, index: number) => {
        const inputValue = event.target.value.trim();
        if (inputValue && !nameRegex.test(inputValue)) {
            toast.warning('Name should contain only letters and spaces.');
            setIsNameValid(false);
        } else {
            setIsNameValid(true);
        }
    };

    const _validateCustomerContactFormContact = (event: any, index: number) => {
        const inputValue = event.target.value.trim();
        if (inputValue && !contactRegex.test(inputValue)) {
            toast.warning('Contact number must contain only digits.');
            setIsContactValid(false); // Set state to invalid
        } else {
            setIsContactValid(true); // Set state to valid
        }
    };

    const _validateCustomerContactFormEmail = (event: any, index: number) => {
        const inputValue = event.target.value.trim();
        if (inputValue && !emailRegex.test(inputValue)) {
            toast.warning('Email must contain letters, numbers, and special characters (e.g., name123@domain.com).');
            setIsEmailValid(false); // Set state to invalid
        } else {
            setIsEmailValid(true); // Set state to valid
        }
    };

    const _validateCustomerContactFormDate = (event: any, index: number) => {
        const inputValue = event.target.value.trim();

        if (inputValue.includes('-') && inputValue.split('-')[0].length === 4) {
            setIsDateValid(true); // Is valid (browser format)
            return;
        }
        if (inputValue && !dateRegex.test(inputValue)) {
            toast.warning('Date must be in format DD/MM/YYYY.');
            setIsDateValid(false); // Set state to invalid
        } else {
            setIsDateValid(true); // Set state to valid
        }
    };

    const _checkNamesEntered = (offeringNameArray: string[]) => {
        let namesEntered = true;

        for (let i = 0; i < offeringNameArray.length; i++) {
            if (offeringNameArray[`${i}`].trim() === '') {
                namesEntered = false;
                break;
            }
        }

        callbackNamesEntered(namesEntered);
    };

    const _addItem = () => {
        addRow(1);

        const customerContactFormNameArray = customerContactFormName.slice();
        const customerContactFormContactArray = customerContactFormContact.slice();
        const customerContactFormEmailArray = customerContactFormEmail.slice();
        const customerContactFormDateArray = customerContactFormDate.slice();

        customerContactFormNameArray.push('');
        customerContactFormContactArray.push('');
        customerContactFormEmailArray.push('');
        customerContactFormDateArray.push('');

        setCustomerContactFormName(customerContactFormNameArray);
        setCustomerContactFormContact(customerContactFormContactArray);
        setCustomerContactFormEmail(customerContactFormEmailArray);
        setCustomerContactFormDate(customerContactFormEmailArray);

        callbackName(customerContactFormNameArray);
        callbackContact(customerContactFormContactArray);
        callbackEmail(customerContactFormEmailArray);
        callbackDate(customerContactFormEmailArray);

        callbackNamesEntered(false);
    };

    const _deleteCartLine = async (index: number): Promise<void> => {
        if (typeof formItems[`${index}`] !== 'undefined') {
            const cartState = await getCartState(actionContext);

            let success = false;

            const cartItems = await getAllBTGPCartLineAsync({ callerContext: actionContext }, cartState.cart.Id);

            if (cartItems.length > 0) {
                const removeCartLinesResult = await cartState.removeCartLines({
                    cartLineIds: [formItems[`${index}`]['cartLineId']]
                });

                if (removeCartLinesResult.status === 'SUCCESS') {
                    success = true;
                }

                const deleteCartLineResult = await deleteBTGPCartLineAsync(
                    { callerContext: actionContext },
                    formItems[`${index}`]['cartLineId']
                );

                if (success && deleteCartLineResult) {
                    deleteRow(index);
                    formItems.splice(index, 1);
                    _updateStateData(index);
                }
            } else {
                deleteRow(index);
                _updateStateData(index);
            }
        } else {
            deleteRow(index);
            _updateStateData(index);
        }
    };

    const _updateStateData = (index: number) => {
        const customerContactFormNameArray = customerContactFormName.slice();
        const customerContactFormContactArray = customerContactFormContact.slice();
        const customerContactFormEmailArray = customerContactFormEmail.slice();
        const customerContactFormDateArray = customerContactFormDate.slice();

        if (typeof customerContactFormNameArray[`${index}`] !== 'undefined') {
            if (customerContactFormNameArray.length > 1) {
                customerContactFormNameArray.splice(index, 1);
            } else {
                customerContactFormNameArray[0] = '';
            }
        }

        if (typeof customerContactFormContactArray[`${index}`] !== 'undefined') {
            if (customerContactFormContactArray.length > 1) {
                customerContactFormContactArray.splice(index, 1);
            } else {
                customerContactFormContactArray[0] = '';
            }
        }

        if (typeof customerContactFormEmailArray[`${index}`] !== 'undefined') {
            if (customerContactFormEmailArray.length > 1) {
                customerContactFormEmailArray.splice(index, 1);
            } else {
                customerContactFormEmailArray[0] = '';
            }
        }

        if (typeof customerContactFormDateArray[`${index}`] !== 'undefined') {
            if (customerContactFormDateArray.length > 1) {
                customerContactFormDateArray.splice(index, 1);
            } else {
                customerContactFormDateArray[0] = '';
            }
        }

        setCustomerContactFormName(customerContactFormNameArray);
        setCustomerContactFormContact(customerContactFormContactArray);
        setCustomerContactFormEmail(customerContactFormEmailArray);
        setCustomerContactFormDate(customerContactFormDateArray);

        callbackName(customerContactFormNameArray);
        callbackContact(customerContactFormContactArray);
        callbackEmail(customerContactFormEmailArray);
        callbackDate(customerContactFormEmailArray);

        _checkNamesEntered(customerContactFormNameArray);
    };

    useEffect(() => {
        const existingContactFormName: string[] = [];
        const existingContactFormContact: string[] = [];
        const existingContactFormEmail: string[] = [];
        const existingContactFormDate: string[] = [];

        const allValid = isNameValid && isContactValid && isEmailValid && isDateValid;

        if (callbackFormValidation) {
            callbackFormValidation(allValid);
        }

        if (formItems.length > 0) {
            for (let i = 0; i < formItems.length; i++) {
                const itemNames: string[] = formItems[`${i}`]['names'];

                // Offering form has only one name, that's why the first element is pushed.
                if (itemNames) {
                    existingContactFormName.push(itemNames[0]);
                }

                existingContactFormContact.push(formItems[`${i}`]['contact']);
                existingContactFormEmail.push(formItems[`${i}`]['email']);
                existingContactFormDate.push(formItems[`${i}`]['date']);
            }

            setCustomerContactFormName(existingContactFormName);
            setCustomerContactFormContact(existingContactFormContact);
            setCustomerContactFormEmail(existingContactFormEmail);
            setCustomerContactFormDate(existingContactFormDate);

            callbackName(existingContactFormName);
            callbackContact(existingContactFormContact);
            callbackEmail(existingContactFormEmail);
            callbackDate(existingContactFormDate);

            _checkNamesEntered(existingContactFormName);
        }
    }, [formItems, isNameValid, isContactValid, isEmailValid, isDateValid, callbackFormValidation]);

    const _buildNameField = (idx: number): JSX.Element => {
        return (
            <div className='row'>
                <div className='col-lg-4 form-field'>
                    <p className='form-label'>{formLabels.name}</p>
                    <input
                        type='text'
                        inputMode='text'
                        value={customerContactFormName[`${idx}`]}
                        aria-live='polite'
                        role='spinbutton'
                        onChange={event => _setCustomerContactFormNameInputValue(event, idx)}
                        onBlur={event => _validateCustomerContactFormName(event, idx)}
                        onKeyDown={event => {
                            if (event.keyCode === 13) {
                                event.preventDefault();
                            }
                        }}
                        className='form-control'
                        maxLength={config.charactersLimitForNames ? config.charactersLimitForNames : 31}
                    />
                </div>
            </div>
        );
    };

    const _buildContact = (idx: number): JSX.Element => {
        return (
            <div className='row'>
                <div className='col-lg-6 form-field'>
                    <p className='form-label'>{formLabels.contact}</p>
                    <input
                        type='text'
                        inputMode='text'
                        value={customerContactFormContact[`${idx}`]}
                        aria-live='polite'
                        role='spinbutton'
                        onChange={event => _setCustomerContactFormContactInputValue(event, idx)}
                        onBlur={event => _validateCustomerContactFormContact(event, idx)}
                        onKeyDown={event => {
                            if (event.keyCode === 13) {
                                event.preventDefault();
                            }
                        }}
                        className='form-control'
                        maxLength={config.charactersLimitForMessages ? config.charactersLimitForMessages : 31}
                    />
                </div>
            </div>
        );
    };

    const _buildEmail = (idx: number): JSX.Element => {
        return (
            <div className='row'>
                <div className='col-lg-6 form-field'>
                    <p className='form-label'>{formLabels.email}</p>
                    <input
                        type='email'
                        inputMode='email'
                        value={customerContactFormEmail[`${idx}`]}
                        aria-live='polite'
                        role='spinbutton'
                        onChange={event => _setcustomerContactFormEmailInputValue(event, idx)}
                        onBlur={event => _validateCustomerContactFormEmail(event, idx)}
                        onKeyDown={event => {
                            if (event.keyCode === 13) {
                                event.preventDefault();
                            }
                        }}
                        className='form-control'
                        maxLength={config.charactersLimitForMessages ? config.charactersLimitForMessages : 31}
                    />
                </div>
            </div>
        );
    };

    const _buildDate = (idx: number): JSX.Element => {
        return (
            <div className='row'>
                <div className='col-lg-6 form-field'>
                    <p className='form-label'>{formLabels.date}</p>
                    <input
                        type='date'
                        inputMode='date'
                        value={customerContactFormDate[`${idx}`]}
                        aria-live='polite'
                        role='spinbutton'
                        onChange={event => _setcustomerContactFormDateInputValue(event, idx)}
                        onBlur={event => _validateCustomerContactFormDate(event, idx)}
                        onKeyDown={event => {
                            if (event.keyCode === 13) {
                                event.preventDefault();
                            }
                        }}
                        className='form-control'
                        maxLength={config.charactersLimitForMessages ? config.charactersLimitForMessages : 31}
                    />
                </div>
            </div>
        );
    };

    return (
        <div className='ecomm-purchase-form'>
            <form onSubmit={handleSubmit(onSubmit)} onReset={reset} autoComplete='off'>
                <div className='ecomm-purchase-form-table'>
                    {rowsData.map((i, idx) => {
                        return (
                            <div key={idx} className='form-record'>
                                <div key={`row${i.id}`} className='form-details row'>
                                    {formScheme.map((element: IField, index: number) => {
                                        if (element.field_name === 'name') {
                                            return (
                                                <div key={index} className='form-details-item col-lg-9'>
                                                    {_buildNameField(idx)}
                                                    {_buildContact(idx)}
                                                    {_buildEmail(idx)}
                                                    {_buildDate(idx)}
                                                </div>
                                            );
                                        }

                                        if (!isMobile && element.field_type === 'product_price') {
                                            return (
                                                <div key={index} className='form-details-item bordered centred col-2'>
                                                    <p key={index} className='subtotal'>
                                                        ${selectedProduct.result?.Price!}
                                                    </p>
                                                </div>
                                            );
                                        }

                                        if (element.field_type === 'action') {
                                            if (isMobile) {
                                                const style = !isEditMode ? 'form-details-item' : 'form-details-item edit';

                                                return (
                                                    <div key={index} className={style}>
                                                        {!isEditMode && allowMultipleRegistrations && (
                                                            <div className='col-10'>
                                                                <button className='add-row' type='submit' onClick={_addItem}>
                                                                    {resource.addAnotherItemTitle[localeCode as 'en' | 'zh']}
                                                                </button>
                                                            </div>
                                                        )}
                                                        <div className='delete-wrapper col-2'>
                                                            <button className={element.action} onClick={() => _deleteCartLine(idx)} />
                                                        </div>
                                                    </div>
                                                );
                                            }

                                            return (
                                                <div key={index} className='form-details-item centred col-sm-1'>
                                                    <button className={element.action} onClick={() => _deleteCartLine(idx)} />
                                                </div>
                                            );
                                        }

                                        return;
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {!isMobile && !isEditMode && allowMultipleRegistrations && (
                    <div className='ecomm-purchase-form-add'>
                        <button type='submit' onClick={_addItem}>
                            {resource.addAnotherItemTitle[localeCode as 'en' | 'zh']}
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
};

export default CustomerContactForm;
